/**
 * SIMILAR BRAND NAMES SERVICE
 *
 * Finds polishCatalog docs whose `brandSlug` is close to the target
 * brand's slug — surfaces catalog entries collectors added under
 * misspelled or reformatted brand names ("Moon Cat", "Mooncat!!!",
 * "moocat"). Brand owners can claim & normalize (rename cascade back
 * to their canonical brand string) or dismiss (mark as "not ours" —
 * excluded from future suggestions).
 *
 * Similarity heuristic — kept simple + inclusive:
 *   - Strip non-alphanumeric characters from both slugs → letters only
 *   - Match if the stripped forms are equal (catches spacing / hyphen
 *     variants: "moon-cat" and "mooncat" both reduce to "mooncat")
 *   - OR Levenshtein distance <= 2 on the stripped forms (catches
 *     typos: "moocat" vs "mooncat")
 *
 * Dismissed variants live on the brand doc's `dismissedBrandNames`
 * array so the exclusion is per-brand and portable.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { slugify } from '../utils/polishSlug';

/** Strip everything except letters and digits, lowercase. */
function stripToAlnum(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Standard Levenshtein distance. Small strings, small cost. */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  const dp: number[][] = Array.from({ length: al + 1 }, () => new Array(bl + 1).fill(0));
  for (let i = 0; i <= al; i++) dp[i][0] = i;
  for (let j = 0; j <= bl; j++) dp[0][j] = j;
  for (let i = 1; i <= al; i++) {
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[al][bl];
}

export interface SimilarBrandMatch {
  /** The brand name string as it appears on polishCatalog docs (with
   *  original casing/punctuation — this is what would be renamed). */
  brand: string;
  /** The brand slug on polishCatalog docs. Different from the target
   *  brand's own slug — otherwise it wouldn't be a "similar" match. */
  brandSlug: string;
  /** Distinct polishCatalog docs under this variant. */
  polishCount: number;
  /** Sample of polish names for the UI to display as a preview.
   *  Capped at 5 so a bad match doesn't blow up the render. */
  samplePolishes: string[];
  /** True if the target brand owner has already dismissed this variant
   *  as "not ours". Returned so the UI can show a re-claim option. */
  isDismissed: boolean;
}

/**
 * Scan polishCatalog for polish docs under brand slugs similar to the
 * target brand's slug. Returns matches grouped by that variant's
 * exact brand string.
 */
export async function findSimilarBrandNames(
  brandName: string,
  dismissedBrandNames: string[] = []
): Promise<SimilarBrandMatch[]> {
  const targetSlug = slugify(brandName);
  const targetStripped = stripToAlnum(brandName);
  if (!targetStripped) return [];

  const dismissedLower = new Set(
    dismissedBrandNames.map((n) => n.toLowerCase())
  );

  // Fetch polishCatalog docs and group by their `brand` field.
  // Bounded because we filter down on the client — no aggregation
  // trigger yet for "brand variant clusters".
  const snap = await getDocs(collection(db, 'polishCatalog'));

  // Group by exact brand string (preserving original casing/punctuation)
  // so we can report a variant's polish count + samples.
  const byBrand = new Map<
    string,
    {
      brand: string;
      brandSlug: string;
      polishCount: number;
      samplePolishes: string[];
    }
  >();

  for (const d of snap.docs) {
    const data = d.data() as Record<string, unknown>;
    const brand = typeof data.brand === 'string' ? data.brand.trim() : '';
    const brandSlug = typeof data.brandSlug === 'string' ? data.brandSlug : '';
    if (!brand || !brandSlug) continue;
    // Skip our own brand.
    if (brandSlug === targetSlug) continue;

    // Similarity check.
    const stripped = stripToAlnum(brand);
    if (!stripped) continue;
    const isSame = stripped === targetStripped;
    const isNearMatch =
      !isSame && editDistance(stripped, targetStripped) <= 2;
    if (!isSame && !isNearMatch) continue;

    // Group by brand string so "Moon Cat" and "MOON CAT" (which strip to
    // the same and slug identically anyway) can collapse — actually we
    // key by brandSlug to avoid casing-only splits.
    const key = brandSlug;
    let entry = byBrand.get(key);
    if (!entry) {
      entry = {
        brand,
        brandSlug,
        polishCount: 0,
        samplePolishes: [],
      };
      byBrand.set(key, entry);
    }
    entry.polishCount++;
    if (entry.samplePolishes.length < 5) {
      const name = typeof data.name === 'string' ? data.name : '';
      if (name) entry.samplePolishes.push(name);
    }
  }

  return Array.from(byBrand.values())
    .map((v) => ({
      ...v,
      isDismissed: dismissedLower.has(v.brand.toLowerCase()),
    }))
    .sort((a, b) => {
      // Non-dismissed first; then by polish count desc.
      if (a.isDismissed !== b.isDismissed) return a.isDismissed ? 1 : -1;
      return b.polishCount - a.polishCount;
    });
}

/** Add a similar brand name to the target brand's dismiss list. */
export async function dismissSimilarBrand(
  brandId: string,
  brandNameToDismiss: string
): Promise<void> {
  const trimmed = brandNameToDismiss.trim();
  if (!trimmed) throw new Error('brandNameToDismiss is required');
  await updateDoc(doc(db, 'brands', brandId), {
    dismissedBrandNames: arrayUnion(trimmed),
  });
}

/** Remove a similar brand name from the dismiss list (re-surface). */
export async function undismissSimilarBrand(
  brandId: string,
  brandNameToRestore: string
): Promise<void> {
  const trimmed = brandNameToRestore.trim();
  if (!trimmed) throw new Error('brandNameToRestore is required');
  await updateDoc(doc(db, 'brands', brandId), {
    dismissedBrandNames: arrayRemove(trimmed),
  });
}

/** Read the current dismiss list for a brand. Used to hydrate the UI. */
export async function getDismissedBrandNames(brandId: string): Promise<string[]> {
  const snap = await getDoc(doc(db, 'brands', brandId));
  if (!snap.exists()) return [];
  const data = snap.data();
  const arr = data?.dismissedBrandNames;
  return Array.isArray(arr) ? arr.filter((n): n is string => typeof n === 'string') : [];
}

